import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { logout } from '../utils/api';
import { toast } from 'sonner';
import { 
  Users, 
  Calendar, 
  Music, 
  TrendingUp, 
  HardDrive, 
  Activity,
  Search,
  Trash2,
  Shield,
  BarChart3,
  LogOut,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import LoadingScreen from '../components/ui/LoadingScreen';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Users data
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  
  // Events data
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  
  // Storage data
  const [storageData, setStorageData] = useState(null);
  
  useEffect(() => {
    loadDashboard();
  }, []);
  
  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'events') {
      loadEvents();
    } else if (activeTab === 'storage') {
      loadStorageStats();
    }
  }, [activeTab, userSearch]);
  
  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès refusé : Réservé aux administrateurs');
        navigate('/dashboard');
      } else {
        toast.error('Erreur lors du chargement du dashboard');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const loadUsers = async () => {
    try {
      const response = await api.get('/admin/users', {
        params: { search: userSearch || undefined }
      });
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    }
  };
  
  const loadEvents = async () => {
    try {
      const response = await api.get('/admin/events');
      setEvents(response.data.events);
      setEventsTotal(response.data.total);
    } catch (error) {
      toast.error('Erreur lors du chargement des événements');
    }
  };
  
  const loadStorageStats = async () => {
    try {
      const response = await api.get('/admin/stats/storage');
      setStorageData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des stats de stockage');
    }
  };
  
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur et toutes ses données ?')) {
      return;
    }
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Utilisateur supprimé');
      loadUsers();
      loadDashboard();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
      return;
    }
    
    try {
      await api.delete(`/admin/events/${eventId}`);
      toast.success('Événement supprimé');
      loadEvents();
      loadDashboard();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  const handleChangeRole = async (userId, newRole) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, null, {
        params: { new_role: newRole }
      });
      toast.success(`Rôle mis à jour: ${newRole}`);
      loadUsers();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du rôle');
    }
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('user');
      toast.success('Déconnexion réussie');
      navigate('/login');
    } catch (error) {
      // Even if logout fails, clear local state and redirect
      localStorage.removeItem('user');
      navigate('/login');
    }
  };
  
  if (loading && !dashboardData) {
    return <LoadingScreen message="Chargement du panneau admin..." />;
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-dark via-dark-card to-dark p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <Shield className="w-8 h-8 text-sunset-orange" />
                Panneau d'Administration
              </h1>
              <p className="text-sunset-orange/70 mt-2">Gestion globale du SaaS DJ Nomad</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-sunset-orange/20 text-sunset-orange hover:bg-sunset-orange/10"
              >
                Retour au Dashboard
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-500/20 text-red-400 hover:bg-red-500/10 flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'users', label: 'Utilisateurs', icon: Users },
            { id: 'events', label: 'Événements', icon: Calendar },
            { id: 'storage', label: 'Stockage', icon: HardDrive }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap
                transition-all duration-200
                ${activeTab === tab.id
                  ? 'bg-sunset-orange text-white shadow-lg'
                  : 'bg-dark-card text-sunset-orange/70 hover:bg-dark-card/80 hover:text-sunset-orange'
                }
              `}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="space-y-6">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && dashboardData && (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-dark-card/80 backdrop-blur-xl border border-sunset-orange/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sunset-orange/70 text-sm font-semibold">Utilisateurs</span>
                    <Users className="w-5 h-5 text-sunset-orange" />
                  </div>
                  <div className="text-3xl font-bold text-white">{dashboardData.kpis.total_users}</div>
                  <div className="text-xs text-green-400 mt-1">
                    +{dashboardData.kpis.new_users_30d} ce mois
                  </div>
                </div>
                
                <div className="bg-dark-card/80 backdrop-blur-xl border border-sunset-orange/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sunset-orange/70 text-sm font-semibold">Événements</span>
                    <Calendar className="w-5 h-5 text-sunset-orange" />
                  </div>
                  <div className="text-3xl font-bold text-white">{dashboardData.kpis.total_events}</div>
                  <div className="text-xs text-blue-400 mt-1">
                    {dashboardData.kpis.live_events} en direct
                  </div>
                </div>
                
                <div className="bg-dark-card/80 backdrop-blur-xl border border-sunset-orange/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sunset-orange/70 text-sm font-semibold">Stockage</span>
                    <HardDrive className="w-5 h-5 text-sunset-orange" />
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {(dashboardData.kpis.storage_used_mb / 1024).toFixed(1)} GB
                  </div>
                  <div className="text-xs text-purple-400 mt-1">
                    {dashboardData.kpis.total_tracks} tracks
                  </div>
                </div>
              </div>
              
              {/* User Growth Chart */}
              <div className="bg-dark-card/80 backdrop-blur-xl border border-sunset-orange/30 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-sunset-orange" />
                  Croissance des utilisateurs (6 derniers mois)
                </h3>
                <div className="flex items-end gap-2 h-40">
                  {dashboardData.user_growth.map((item, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full bg-gradient-to-t from-sunset-orange to-sunset-secondary rounded-t"
                        style={{ height: `${(item.count / Math.max(...dashboardData.user_growth.map(i => i.count))) * 100}%` }}
                      />
                      <div className="text-xs text-sunset-orange/70 mt-2 text-center">
                        {item.month.split(' ')[0].slice(0, 3)}
                      </div>
                      <div className="text-xs text-white font-semibold">{item.count}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Recent Activity */}
              <div className="bg-dark-card/80 backdrop-blur-xl border border-sunset-orange/30 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-sunset-orange" />
                  Activité récente
                </h3>
                <div className="space-y-2">
                  {dashboardData.recent_activity.map((event) => (
                    <div key={event.id} className="flex items-center justify-between p-3 bg-dark-input/30 rounded-lg">
                      <div>
                        <div className="text-white font-medium">{event.name}</div>
                        <div className="text-xs text-sunset-orange/70">
                          {new Date(event.created_at).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                      <span className={`
                        px-2 py-1 rounded-full text-xs font-semibold
                        ${event.status === 'live' ? 'bg-green-500/20 text-green-300' : ''}
                        ${event.status === 'draft' ? 'bg-yellow-500/20 text-yellow-300' : ''}
                        ${event.status === 'ended' ? 'bg-gray-500/20 text-gray-300' : ''}
                      `}>
                        {event.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          
          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="bg-dark-card/80 backdrop-blur-xl border border-sunset-orange/30 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">
                  Utilisateurs ({usersTotal})
                </h3>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-sunset-orange/50" />
                    <Input
                      placeholder="Rechercher..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-9 bg-dark-input/50 border-sunset-orange/20 text-white"
                    />
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-sunset-orange/20">
                      <th className="text-left text-sunset-orange/70 text-sm font-semibold p-3">Nom</th>
                      <th className="text-left text-sunset-orange/70 text-sm font-semibold p-3">Email</th>
                      <th className="text-left text-sunset-orange/70 text-sm font-semibold p-3">Rôle</th>
                      <th className="text-left text-sunset-orange/70 text-sm font-semibold p-3">Stats</th>
                      <th className="text-left text-sunset-orange/70 text-sm font-semibold p-3">Inscrit</th>
                      <th className="text-left text-sunset-orange/70 text-sm font-semibold p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-sunset-orange/10 hover:bg-sunset-orange/5">
                        <td className="text-white p-3">{user.name}</td>
                        <td className="text-white p-3">{user.email}</td>
                        <td className="p-3">
                          <select
                            value={user.role}
                            onChange={(e) => handleChangeRole(user.id, e.target.value)}
                            className="bg-dark-input border border-sunset-orange/20 text-white rounded px-2 py-1 text-sm"
                          >
                            <option value="host">Host</option>
                            <option value="dj">DJ</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="text-sunset-orange/70 p-3 text-sm">
                          {user.stats.events} events, {user.stats.tracks} tracks
                        </td>
                        <td className="text-sunset-orange/70 p-3 text-sm">
                          {new Date(user.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="p-3">
                          <Button
                            onClick={() => handleDeleteUser(user.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="bg-dark-card/80 backdrop-blur-xl border border-sunset-orange/30 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6">
                Événements ({eventsTotal})
              </h3>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-sunset-orange/20">
                      <th className="text-left text-sunset-orange/70 text-sm font-semibold p-3">Nom</th>
                      <th className="text-left text-sunset-orange/70 text-sm font-semibold p-3">Propriétaire</th>
                      <th className="text-left text-sunset-orange/70 text-sm font-semibold p-3">Statut</th>
                      <th className="text-left text-sunset-orange/70 text-sm font-semibold p-3">Date</th>
                      <th className="text-left text-sunset-orange/70 text-sm font-semibold p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event) => (
                      <tr key={event.id} className="border-b border-sunset-orange/10 hover:bg-sunset-orange/5">
                        <td className="text-white p-3">{event.name}</td>
                        <td className="text-white p-3">
                          <div>{event.owner_name}</div>
                          <div className="text-xs text-sunset-orange/70">{event.owner_email}</div>
                        </td>
                        <td className="p-3">
                          <span className={`
                            px-2 py-1 rounded-full text-xs font-semibold
                            ${event.status === 'live' ? 'bg-green-500/20 text-green-300' : ''}
                            ${event.status === 'draft' ? 'bg-yellow-500/20 text-yellow-300' : ''}
                            ${event.status === 'ended' ? 'bg-gray-500/20 text-gray-300' : ''}
                          `}>
                            {event.status}
                          </span>
                        </td>
                        <td className="text-sunset-orange/70 p-3 text-sm">
                          {new Date(event.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="p-3">
                          <Button
                            onClick={() => handleDeleteEvent(event.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Storage Tab */}
          {activeTab === 'storage' && storageData && (
            <div className="bg-dark-card/80 backdrop-blur-xl border border-sunset-orange/30 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6">
                Statistiques de stockage
              </h3>
              
              <div className="mb-6">
                <div className="text-sm text-sunset-orange/70 mb-2">Stockage total estimé</div>
                <div className="text-3xl font-bold text-white">
                  {(storageData.total_estimated_mb / 1024).toFixed(2)} GB
                </div>
                <div className="text-xs text-sunset-orange/70 mt-1">
                  {storageData.total_tracks} tracks au total
                </div>
              </div>
              
              <h4 className="text-white font-semibold mb-4">Top 10 utilisateurs (stockage)</h4>
              <div className="space-y-2">
                {storageData.top_users.map((user, index) => (
                  <div key={user.user_id} className="flex items-center justify-between p-3 bg-dark-input/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-sunset-orange font-bold">#{index + 1}</span>
                      <div>
                        <div className="text-white font-medium">{user.user_name}</div>
                        <div className="text-xs text-sunset-orange/70">{user.user_email}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-semibold">{user.track_count} tracks</div>
                      <div className="text-xs text-sunset-orange/70">
                        {(user.estimated_storage_mb / 1024).toFixed(2)} GB
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
